import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useAutosave } from '../../hooks/useAutosave.js'
import SavingIndicator from '../../components/SavingIndicator.jsx'

/**
 * Single-line "Chief complaint" input — what the patient is presenting with
 * in the patient's own words. Written to `Encounter.chief_complaint` and
 * surfaced verbatim as Section C of the consolidated summary / printable doc.
 *
 * Mirrors NarrativeEditor's autosave + echo-proofing pattern so that:
 *  - keystrokes commit ~1.2 s after typing stops
 *  - the parent's "Save draft" button can flush a pending save via ref
 *  - the server's POST echo never stomps in-flight typing
 */
const ChiefComplaintInput = forwardRef(function ChiefComplaintInput(
  { value, disabled, onSave, saveState }, ref,
) {
  const [text, setText] = useState(value || '')
  const savedRef = useRef(value || '')

  useEffect(() => {
    const incoming = value || ''
    if (incoming === savedRef.current) return  // server echo — ignore
    setText(incoming)
    savedRef.current = incoming
  }, [value])

  const flush = useAutosave(text, (t) => {
    if (t !== savedRef.current) {
      savedRef.current = t
      onSave(t)
    }
  }, 1200)

  useImperativeHandle(ref, () => ({ flush }), [flush])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-semibold text-slate-800" htmlFor="chief-complaint">
          Chief complaint
        </label>
        <SavingIndicator state={saveState} />
      </div>
      <p className="text-[11px] text-slate-500 mb-2">
        One short line in the patient's own words — the reason for today's visit.
        Appears as Section C on the printable summary.
      </p>
      <input
        id="chief-complaint"
        type="text"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. chest pain for 2 days, fever and cough, dizziness…"
        maxLength={250}
        className="w-full border rounded px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  )
})

export default ChiefComplaintInput
